import { type PaletteOptions } from '@mui/material';
import React, { createContext, useReducer } from 'react';

import type { RoutineEvent } from '@models/routine';

type ThemeMode = 'light' | 'dark' | 'system';

type State = {
  completedRoutines: Record<string, Record<string, string[]>>;
  routine: RoutineEvent | undefined;
  /**
   * Necessary hacky evil. Determines if the routine being added is a single event
   */
  isSingleRoutine: boolean | undefined;
  customPalette: PaletteOptions | undefined;
  themeMode: ThemeMode;
};

enum AppActions {
  SetCompletedRoutines = 'setCompletedRoutines',
  SelectRoutine = 'selectRoutine',
  SetIsSingleRoutine = 'setIsSingleRoutine',
  Reset = 'reset',
  SetCustomPalette = 'SetCustomPalette',
  SetThemeMode = 'SetThemeMode',
}

type Payload = {
  [AppActions.SetCompletedRoutines]: {
    data: Record<string, Record<string, string[]>>;
  };
  [AppActions.SelectRoutine]: {
    routine: RoutineEvent | undefined;
  };
  [AppActions.SetIsSingleRoutine]: {
    isSingleRoutine: boolean | undefined;
  };
  [AppActions.SetCustomPalette]: {
    customPalette: PaletteOptions;
  };
  [AppActions.SetThemeMode]: {
    themeMode: ThemeMode;
  };
  [AppActions.Reset]: undefined;
};
const initialState: State = {
  completedRoutines: {},
  routine: undefined,
  isSingleRoutine: undefined,
  customPalette: undefined,
  themeMode: 'light',
};

function reducer(state: State, action: Action<Payload>): State {
  switch (action.type) {
    case AppActions.SelectRoutine:
      return {
        ...state,
        routine: action.routine,
      };
    case AppActions.SetCompletedRoutines:
      return {
        ...state,
        completedRoutines: action.data,
      };
    case AppActions.SetIsSingleRoutine:
      return {
        ...state,
        isSingleRoutine: action.isSingleRoutine,
      };
    case AppActions.SetCustomPalette:
      return { ...state, customPalette: action.customPalette };
    case AppActions.Reset:
      return initialState;
    case AppActions.SetThemeMode:
      return { ...state, themeMode: action.themeMode };
    default:
      throw new Error('App reducer case not found');
  }
}

const AppContext = createContext<{
  state: State;
  dispatch: React.Dispatch<Action<Payload>>;
}>({ state: initialState, dispatch: () => null });

function AppProvider({ children }: { children: React.ReactNode }) {
  const [state, dispatch] = useReducer(reducer, initialState);

  const stateKey = JSON.stringify(state);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const value = React.useMemo(() => ({ state, dispatch }), [stateKey]);

  React.useEffect(() => {
    try {
      const stored =
        typeof window !== 'undefined'
          ? localStorage.getItem('themeMode')
          : null;
      const valid =
        stored === 'light' || stored === 'dark' || stored === 'system';

      const themeMode: ThemeMode = valid ? (stored as ThemeMode) : 'light';

      if (state.themeMode !== themeMode) {
        dispatch({ type: AppActions.SetThemeMode, themeMode });
      }
    } catch {
      const fallback = 'light';
      if (state.themeMode !== fallback) {
        dispatch({ type: AppActions.SetThemeMode, themeMode: fallback });
      }
    }
  }, [state.themeMode]);

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
}

export { AppProvider, AppContext };
export { AppActions };
export type { ThemeMode };
